/**
 * RemoveStopDialog - Dialog for removing a stop with status selection
 *
 * Allows user to choose what status the Werk_Order should get:
 * - Alleen deze rit verwijderen (status ongewijzigd) — voor een foutieve of
 *   dubbele rit: haal de stop van de route af, maar laat de werk order (status
 *   én tijdvakken) exact zoals hij is. Bij een gedetecteerd duplicaat is dit de
 *   default; de andere stop houdt de werk order ingepland.
 * - Ophalen plannen / Terugbrengen plannen (for rescheduling)
 * - Geannuleerd (if order is cancelled)
 *
 * Reparateur-stops (kaart 10 fase 3 plak 3, ontwerpbesluit 3): geen
 * werkorder, dus geen statuskeuzes — een eigen bevestigingsvariant die de
 * KEEP-sentinel stuurt (Apex raakt zonder WO toch niets aan).
 */

import { useState, useCallback } from 'react';
import type { RouteStop } from '../../lib/salesforce/types';

// Sentinel value telling the backend to remove the stop WITHOUT mutating the
// werk order (status + time windows stay intact). Used for duplicate stops.
export const KEEP_STATUS_SENTINEL = '__KEEP__';

interface RemoveStopDialogProps {
  stop: RouteStop;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newStatus: string) => void;
  isRemoving: boolean;
  /**
   * True when this werk order appears more than once on the route (a duplicate
   * stop). Then removing the stop without touching the werk order is the
   * intended default — the other stop keeps it scheduled. The keep-status option
   * is always offered (also for a foutieve/losse rit), this only drives the
   * default selection and the wording.
   */
  isDuplicate?: boolean;
}

export function RemoveStopDialog({
  stop,
  isOpen,
  onClose,
  onConfirm,
  isRemoving,
  isDuplicate = false,
}: RemoveStopDialogProps) {
  const isReparateurStop = stop.isReparateurStop === true;
  // Default status based on stop type
  const defaultStatus = stop.stopType === 'Terugbrengen' ? 'Terugbrengen plannen' : 'Ophalen plannen';
  // For a detected duplicate, keeping the werk order untouched is the intended
  // action. For a non-duplicate we default to rescheduling so a single-stop
  // werk order is never accidentally orphaned — the user can still pick keep.
  const [selectedStatus, setSelectedStatus] = useState(
    isDuplicate ? KEEP_STATUS_SENTINEL : defaultStatus,
  );

  const handleConfirm = useCallback(() => {
    // Reparateur-stop: geen werkorder om te muteren — stuur de KEEP-sentinel.
    onConfirm(isReparateurStop ? KEEP_STATUS_SENTINEL : selectedStatus);
  }, [selectedStatus, onConfirm, isReparateurStop]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Stop verwijderen
        </h3>

        <p className="text-sm text-gray-600 mb-4">
          Je staat op het punt om <span className="font-medium">{stop.accountName}</span> te verwijderen van de route.
        </p>

        {isReparateurStop && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm font-medium text-amber-800">Reparateur-stop</p>
            <p className="text-xs text-amber-700 mt-1">
              Deze stop heeft geen werkorder; er is dus geen status om te kiezen.
              Reparatie-kleden die aan deze stop hingen verliezen hun rit-koppeling.
              Plan ze opnieuw in via de reparatie-pagina.
            </p>
          </div>
        )}

        {isReparateurStop ? null : (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nieuwe status voor de werk order:
          </label>
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="newStatus"
                value={KEEP_STATUS_SENTINEL}
                checked={selectedStatus === KEEP_STATUS_SENTINEL}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-4 h-4 text-green-600 focus:ring-green-500"
              />
              <div>
                <div className="font-medium text-gray-900">Alleen deze rit verwijderen</div>
                <div className="text-xs text-gray-500">
                  {isDuplicate
                    ? 'Dubbele stop — werk order blijft ongewijzigd'
                    : 'Foutieve/dubbele rit — werk order (status én tijdvakken) blijft ongewijzigd'}
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="newStatus"
                value={defaultStatus}
                checked={selectedStatus === defaultStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <div className="font-medium text-gray-900">{defaultStatus}</div>
                <div className="text-xs text-gray-500">
                  Werk order kan opnieuw worden ingepland
                </div>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="newStatus"
                value="Geannuleerd"
                checked={selectedStatus === 'Geannuleerd'}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-4 h-4 text-red-600 focus:ring-red-500"
              />
              <div>
                <div className="font-medium text-gray-900">Geannuleerd</div>
                <div className="text-xs text-gray-500">
                  Werk order wordt geannuleerd
                </div>
              </div>
            </label>
          </div>
        </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isRemoving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isRemoving}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isRemoving ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verwijderen...
              </>
            ) : (
              'Verwijderen'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
